import {trackCustomerData} from '@aboutyou/legacy-tracking-sdk';
import {
  useBasketStateItems,
  useBasketStateTotalQuantity,
  useIsBasketStateLoaded,
  useIsWishlistLoaded,
  useWishlistCount,
  useWishlistProductIds,
} from '@aboutyou/user';
import {waitUntil} from '@aboutyou/utils';
import React, {FC, memo, useEffect, useMemo} from 'react';
import {useTrackAbTest} from '../../trackingHooks/trackAbTest';
import {useTracking} from '../../utils/utils';
import {useConvertUserAndSessionToCustomer} from './useConvertUserAndSessionToCustomer';

const TrackCustomer: FC = () => {
  if (!process.browser) {
    return null;
  }

  return <StartCustomerTracking />;
};

const StartCustomerTracking = () => {
  useTrackCustomerData();
  return null;
};

/**
 * Observes Basket, Wishlist and Customer data and fires a Customer Data event as soon as they are loaded and the initial page view is tracked. The order matters here as otherwise the data is wrong
 * @deprecated This should be refactored. We just implemented this for the launch but don't consider this a good api.
 */
const useTrackCustomerData = () => {
  const {
    TrackingClient,
    shouldFireCustomerDataEvent,
    resetCustomerDataEventScheduler,
    scheduleCustomerDataEvent,
    fireCustomerDataCallback,
  } = useTracking();
  const customer = useConvertUserAndSessionToCustomer();
  const totalQuantity = useBasketStateTotalQuantity();
  const isBasketStateLoaded = useIsBasketStateLoaded();
  const basketStateItems = useBasketStateItems();
  const {count: wishlistCount} = useWishlistCount();
  const isWishlistLoaded = useIsWishlistLoaded();
  const wishlistProductIds = useWishlistProductIds();

  const basketItems = useMemo(
    () =>
      basketStateItems.map(item => ({
        id: item.productId,
        quantity: item.quantity,
        sizeId: item.sizeId,
      })),
    [basketStateItems],
  );

  const wishlistItems = useMemo(
    () => ({
      items: wishlistProductIds.map(id => ({
        item_id: id,
      })),
    }),
    [wishlistProductIds],
  );

  useTrackAbTest();

  // when the user logs in, we want to track the customer_data event with the updated event.
  useEffect(() => {
    scheduleCustomerDataEvent();
  }, [customer?.type]);

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    if (!shouldFireCustomerDataEvent()) {
      return;
    }

    if (customer && isBasketStateLoaded && isWishlistLoaded) {
      const {waiter, cancel} = waitUntil(() =>
        window.dataLayer.some(
          (item: any) =>
            item.event === 'contentView' || item.event === 'content_view',
        ),
      );

      waiter.then(() => {
        TrackingClient.track(
          trackCustomerData({
            customer,
            basketCount: totalQuantity,
            wishlistCount,
            wishlist: wishlistItems,
            basketItems,
          }),
        );
        fireCustomerDataCallback();
        resetCustomerDataEventScheduler();
      });
      return cancel;
    }
  }, [
    TrackingClient,
    JSON.stringify(customer),
    totalQuantity,
    wishlistCount,
    isWishlistLoaded,
    shouldFireCustomerDataEvent,
    resetCustomerDataEventScheduler,
    isBasketStateLoaded,
    wishlistItems,
    basketItems,
  ]);
};

const MemoizedTrackCustomer = memo(TrackCustomer);

export {MemoizedTrackCustomer as TrackCustomer};
